import { useReducer, useEffect, useCallback } from 'react';

// ─────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────

export const GAME_TYPES = [
  'Regular Season',
  'Tournament',
  'Playoff',
  'Scrimmage',
  'Fall Ball',
  'All-Star',
];
export const DECISIONS = ['W', 'L', 'S', 'H', 'BS', 'ND'];
export const POSITIONS = [
  'P',
  'C',
  '1B',
  '2B',
  '3B',
  'SS',
  'LF',
  'CF',
  'RF',
  'DH',
];
export const PITCH_TYPES = ['FB', 'CB', 'SL', 'CT', 'CH', 'SP', 'OTHER'];

export function hasRISP(baseState) {
  return !!(baseState.second || baseState.third);
}

export function runnersOnCount(baseState) {
  return (
    (baseState.first ? 1 : 0) +
    (baseState.second ? 1 : 0) +
    (baseState.third ? 1 : 0)
  );
}

// Suggested max RBI for outcome prompt pre-fill
export function maxRBI(baseState, outcome) {
  const runners = runnersOnCount(baseState);
  if (outcome === 'hr' || outcome === 'iphr') return runners + 1;
  return runners;
}

// ─────────────────────────────────────────────
// INITIAL STATE
// ─────────────────────────────────────────────

const INITIAL_BASE_STATE = { first: false, second: false, third: false };

const INITIAL_PITCHING = {
  strikeCounter: 0,
  ballCounter: 0,
  stats: {
    outs: 0,
    pitchCount: 0,
    strikeouts: 0,
    walks: 0,
    ibb: 0,
    hits: 0,
    hr: 0, // home runs allowed — required for FIP
    hbp: 0, // batters hit — required for FIP
    runs: 0, // MANUAL: parent taps +R
    earnedRuns: 0, // MANUAL: parent taps +ER
    wildPitches: 0,
    balks: 0,
    pickoffs: 0,
    pickoffAttempts: 0,
    pickoffOuts: 0,
    pickoffDetails: [],
    csAttempts: 0,
    csOuts: 0,
    csDetails: [],
    firstPitchStrikes: 0,
    firstPitchBalls: 0,
  },
  velocities: [],
  actions: [],
  role: 'Starter', // 'Starter' | 'Relief'
  pitchingStarted: false,
  reliefInning: '',
  inheritedRunners: 0, // runners on base when relief pitcher entered
  inheritedBases: [], // which bases: ['1B', '2B'] etc
  inheritedRunnersScored: 0, // of those inherited runners, how many scored (NOT this pitcher's ER)
  decision: null, // W | L | S | H | BS | ND | null
  inningBreakdowns: [], // per-inning { inning, strikes, balls, pitches, strikePct }
  pitchingDone: false,
  currentInning: 1,
  pendingInningEnd: false,
  phantomOuts: 0,
  opponentBaseState: {
    first:  null,
    second: null,
    third:  null,
  },
  inningPoisoned: false,
};

const INITIAL_BATTING = {
  stats: {
    pa: 0,
    ab: 0,
    h: 0,
    singles: 0,
    doubles: 0,
    triples: 0,
    hr: 0,
    iphr: 0,
    runs: 0, // R scored — did my player cross the plate?
    rbi: 0,
    bb: 0,
    ibb: 0,
    hbp: 0,
    k: 0,
    kLooking: 0,
    kSwinging: 0,
    sb: 0,
    cs: 0,
    csAttempts: 0,
    csOuts: 0,
    csDetails: [],
    pickedOff: 0,
    pickoffAttempts: 0,
    pickoffOuts: 0,
    pickoffDetails: [],
    gidp: 0,
    sf: 0,
    sh: 0,
    roe: 0,
    fc: 0,
    d3k_reached: 0,
    d3k_out: 0,
    ci: 0,
    ab_risp: 0,
    h_risp: 0,
    rbi_risp: 0,
  },
  actions: [],
  status: 'Starting',
  substituteInning: '',
  // Guided "New At Bat" flow
  atBatPhase: 'idle', // 'idle' | 'active'
  playerPosition: null,
  currentAtBatId: null,
  currentAtBat: {
    baseState: INITIAL_BASE_STATE,
    risp: false,
    strikeCounter: 0,
    ballCounter: 0,
  },
  // Persisted per-at-bat tracking prefs — carry forward automatically
  trackingPrefs: {
    count: true,
    strikeDetail: true,
    hitType: true,
    hitLocation: true,
    strikeoutType: true,
  },
};

const INITIAL_FIELDING = {
  positions: {},
  actions: [],
  activePosition: null,
};

export const INITIAL_STATE = {
  playerId: null,
  gameId: null,
  playerName: '',
  playerIsPitcher: false,
  playerIsPitcherOnly: false,
  playerPitches: [],
  gameDate: new Date().toISOString().split('T')[0],
  opponent: '',
  sport: 'baseball',
  homeAway: 'home',
  gameType: 'Regular Season',
  teamScore: null,
  opponentScore: null,
  venue: '',
  notes: '',
  pitching: INITIAL_PITCHING,
  batting: INITIAL_BATTING,
  fielding: INITIAL_FIELDING,
  tracking: {
    pitchTypes: false,
    velocity: false,
    popFlyType: true, // always on — distinguish foul popup vs fair popup
  },
};

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

function generateId() {
  return Date.now() + Math.random();
}

function recalculateCount(actions) {
  let strikes = 0;
  let balls = 0;

  for (const action of actions) {
    if (action.type === 'strike') {
      if (action.detail === 'foul' && strikes >= 2) {
        // foul at 2 strikes — no change
      } else if (action.detail === 'foulTip' && strikes >= 2) {
        // caught foul tip on strike 3 = K → reset
        strikes = 0;
        balls = 0;
      } else {
        strikes++;
        if (strikes >= 3) {
          strikes = 0;
          balls = 0;
        }
      }
    } else if (action.type === 'ball' || action.type === 'wildPitch') {
      balls++;
      if (balls >= 4) {
        strikes = 0;
        balls = 0;
      }
    } else if (action.type === 'ballInPlay' || action.type === 'ibb' || action.type === 'd3k_reached' || action.type === 'd3k_out' || action.type === 'hbp') {
      strikes = 0;
      balls = 0;
    }
  }
  return { strikes, balls };
}

/**
 * Replays pitching actions to derive all stats.
 *
 * R and ER are MANUAL — the reducer has adjustRuns/adjustEarnedRuns action types.
 * Reason: earned/unearned determination requires full inning context (errors, passed balls
 * for the whole team) which a single-player tracker cannot know.
 *
 * HR allowed and HBP are tracked as distinct action types for FIP calculation.
 * foulTip on 2 strikes = K (not a regular foul ball).
 * firstPitchStrikes/Balls tracks only the first pitch thrown to each batter.
 * inningBreakdowns provides per-inning command data for fatigue analysis.
 */
function recalculatePitchingStats(actions, currentInning) {
  const stats = {
    outs: 0,
    pitchCount: 0,
    strikeouts: 0,
    walks: 0,
    ibb: 0,
    hits: 0,
    hr: 0,
    hbp: 0,
    runs: 0,
    earnedRuns: 0,
    wildPitches: 0,
    balks: 0,
    pickoffs: 0,
    pickoffAttempts: 0,
    pickoffOuts: 0,
    pickoffDetails: [],
    csAttempts: 0,
    csOuts: 0,
    csDetails: [],
    firstPitchStrikes: 0,
    firstPitchBalls: 0,
  };
  const velocities = [];
  const inningMap = {};

  let strikes = 0;
  let balls = 0;
  let isFirstPitch = true; // reset after each batter ends

  for (const action of actions) {
    // ── Manual R/ER overrides ─────────────────
    if (action.type === 'adjustRuns') {
      stats.runs = Math.max(0, action.value);
      continue;
    }
    if (action.type === 'adjustEarnedRuns') {
      stats.earnedRuns = Math.max(0, action.value);
      continue;
    }
    if (action.type === 'inheritedRunnerScored') continue; // tracked separately on state

    // ── Non-pitch discrete events ─────────────
    if (action.type === 'balk') {
      stats.balks++;
      continue;
    }
    if (action.type === 'pickoff') {
      stats.pickoffs++;
      stats.pickoffAttempts++;
      if (action.result === 'out') {
        stats.pickoffOuts++;
        stats.outs++;
        const poInning = action.inning || currentInning || 1;
        if (!inningMap[poInning]) inningMap[poInning] = { inning: poInning, strikes: 0, balls: 0, pitches: 0, outs: 0 };
        inningMap[poInning].outs++;
      }
      if (!stats.pickoffDetails) stats.pickoffDetails = [];
      stats.pickoffDetails.push({
        base: action.base || null,
        thrower: action.thrower || null,
        result: action.result || 'out',
      });
      continue;
    }
    if (action.type === 'sb') {
      stats.sbAllowed = (stats.sbAllowed || 0) + 1;
      continue;
    }
    if (action.type === 'cs') {
      stats.csAttempts = (stats.csAttempts || 0) + 1;
      stats.csOuts = (stats.csOuts || 0) + 1;
      stats.outs++;
      const csInning = action.inning || currentInning || 1;
      if (!inningMap[csInning]) inningMap[csInning] = { inning: csInning, strikes: 0, balls: 0, pitches: 0, outs: 0 };
      inningMap[csInning].outs++;
      if (!stats.csDetails) stats.csDetails = [];
      stats.csDetails.push({
        base: action.base || null,
        thrower: action.thrower || null,
        tagger: action.tagger || null,
        result: 'out',
      });
      continue;
    }
    if (action.type === 'fcOut' || action.type === 'advancementOut') {
      stats.outs++;
      const aoInning = action.inning || currentInning || 1;
      if (!inningMap[aoInning]) inningMap[aoInning] = { inning: aoInning, strikes: 0, balls: 0, pitches: 0, outs: 0 };
      inningMap[aoInning].outs++;
      continue;
    }
    if (action.type === 'd3k_reached') {
      stats.strikeouts++;
      const d3krInning = action.inning || currentInning || 1;
      if (!inningMap[d3krInning]) inningMap[d3krInning] = { inning: d3krInning, strikes: 0, balls: 0, pitches: 0, outs: 0 };
      strikes = 0;
      balls = 0;
      isFirstPitch = true;
      continue;
    }
    if (action.type === 'd3k_out') {
      stats.strikeouts++;
      stats.outs++;
      const d3koInning = action.inning || currentInning || 1;
      if (!inningMap[d3koInning]) inningMap[d3koInning] = { inning: d3koInning, strikes: 0, balls: 0, pitches: 0, outs: 0 };
      inningMap[d3koInning].outs++;
      strikes = 0;
      balls = 0;
      isFirstPitch = true;
      continue;
    }
    if (action.type === 'hbp') {
      stats.hbp++;
      isFirstPitch = true;
      continue;
    }
    if (action.type === 'ibb') {
      stats.ibb++;
      stats.walks++;
      isFirstPitch = true;
      continue;
    }

    // ── Only pitch events below this line ─────
    const isPitch = ['strike', 'ball', 'ballInPlay', 'wildPitch'].includes(
      action.type
    );
    if (!isPitch) continue;

    const inning = action.inning || currentInning || 1;
    if (!inningMap[inning])
      inningMap[inning] = { inning, strikes: 0, balls: 0, pitches: 0, outs: 0 };

    stats.pitchCount++;
    inningMap[inning].pitches++;

    if (action.velocity) velocities.push(action.velocity);

    // First-pitch-of-batter tracking
    if (isFirstPitch) {
      if (action.type === 'strike' || action.type === 'ballInPlay') {
        stats.firstPitchStrikes++;
        inningMap[inning].strikes++;
      } else {
        stats.firstPitchBalls++;
        inningMap[inning].balls++;
      }
      isFirstPitch = false;
    }

    // ── Count and stat logic ──────────────────
    if (action.type === 'strike') {
      if (action.detail === 'foul' && strikes >= 2) {
        inningMap[inning].strikes++; // count for command %, no count change
      } else if (action.detail === 'foulTip' && strikes >= 2) {
        stats.strikeouts++;
        stats.outs++;
        inningMap[inning].strikes++;
        inningMap[inning].outs++;
        strikes = 0;
        balls = 0;
        isFirstPitch = true;
      } else {
        strikes++;
        inningMap[inning].strikes++;
        if (strikes >= 3) {
          stats.strikeouts++;
          stats.outs++;
          inningMap[inning].outs++;
          strikes = 0;
          balls = 0;
          isFirstPitch = true;
        }
      }
    } else if (action.type === 'ball') {
      balls++;
      inningMap[inning].balls++;
      if (balls >= 4) {
        stats.walks++;
        strikes = 0;
        balls = 0;
        isFirstPitch = true;
      }
    } else if (action.type === 'wildPitch') {
      stats.wildPitches++;
      balls++;
      inningMap[inning].balls++;
      if (balls >= 4) {
        stats.walks++;
        strikes = 0;
        balls = 0;
        isFirstPitch = true;
      }
    } else if (action.type === 'ballInPlay') {
      inningMap[inning].strikes++; // contact = strike thrown
      const d = action.detail;
      if (d === 'out' || d === 'popfly_fair' || d === 'popfly_foul') {
        stats.outs++;
        inningMap[inning].outs++;
      } else if (d === 'hr') {
        stats.hits++;
        stats.hr++;
        // Runs NOT auto-added — parent uses +R / +ER buttons
      } else if (d !== 'error' && d !== 'fc') {
        stats.hits++;
      }
      strikes = 0;
      balls = 0;
      isFirstPitch = true;
    }
  }

  const inningBreakdowns = Object.values(inningMap)
    .sort((a, b) => a.inning - b.inning)
    .map((d) => ({
      ...d,
      strikePct: d.pitches > 0 ? Math.round((d.strikes / d.pitches) * 100) : 0,
    }));

  return { stats, velocities, inningBreakdowns };
}

function recalculateBattingStats(actions) {
  const stats = {
    pa: 0,
    ab: 0,
    h: 0,
    singles: 0,
    doubles: 0,
    triples: 0,
    hr: 0,
    iphr: 0,
    runs: 0,
    rbi: 0,
    bb: 0,
    ibb: 0,
    hbp: 0,
    k: 0,
    kLooking: 0,
    kSwinging: 0,
    sb: 0,
    cs: 0,
    csAttempts: 0,
    csOuts: 0,
    csDetails: [],
    pickedOff: 0,
    pickoffAttempts: 0,
    pickoffOuts: 0,
    pickoffDetails: [],
    gidp: 0,
    sf: 0,
    sh: 0,
    roe: 0,
    fc: 0,
    d3k_reached: 0,
    d3k_out: 0,
    ci: 0,
    ab_risp: 0,
    h_risp: 0,
    rbi_risp: 0,
  };

  for (const action of actions) {
    const { outcome, risp, strikeoutType } = action;
    // Count-only actions — not a PA, skip entirely
    if (outcome === 'count_ball' || outcome === 'count_strike') continue;
    // Baserunning-only events — not a PA
    if (outcome === 'run') {
      stats.runs++;
      continue;
    }
    if (outcome === 'sb') {
      stats.sb++;
      continue;
    }
    if (outcome === 'cs') {
      stats.cs++;
      stats.csAttempts++;
      stats.csOuts++;
      if (!stats.csDetails) stats.csDetails = [];
      stats.csDetails.push({
        base: action.base || null,
        thrower: action.thrower || null,
        result: action.result || 'out',
      });
      continue;
    }
    if (outcome === 'pickedOff') {
      stats.pickedOff++;
      continue;
    }

    // Everything else = plate appearance
    stats.pa++;
    let countsAsAB = true;
    const rbi = action.rbi || 0;

    switch (outcome) {
      case 'single':
        stats.singles++;
        stats.h++;
        stats.rbi += rbi;
        if (risp) {
          stats.h_risp++;
          stats.rbi_risp += rbi;
        }
        break;
      case 'double':
        stats.doubles++;
        stats.h++;
        stats.rbi += rbi;
        if (risp) {
          stats.h_risp++;
          stats.rbi_risp += rbi;
        }
        break;
      case 'triple':
        stats.triples++;
        stats.h++;
        stats.rbi += rbi;
        if (risp) {
          stats.h_risp++;
          stats.rbi_risp += rbi;
        }
        break;
      case 'hr':
        stats.hr++;
        stats.h++;
        stats.rbi += rbi || 1;
        if (risp) {
          stats.h_risp++;
          stats.rbi_risp += rbi || 1;
        }
        break;
      case 'iphr':
        stats.iphr++;
        stats.hr++;
        stats.h++;
        stats.rbi += rbi || 1;
        if (risp) {
          stats.h_risp++;
          stats.rbi_risp += rbi || 1;
        }
        break;
      case 'out':
        stats.rbi += rbi;
        break;
      case 'gidp':
        stats.gidp++;
        stats.rbi += rbi;
        break;
      case 'k':
        stats.k++;
        if (strikeoutType === 'looking') stats.kLooking++;
        if (strikeoutType === 'swinging') stats.kSwinging++;
        break;
      case 'd3k_reached':
        stats.d3k_reached++;
        stats.k++;
        if (strikeoutType === 'looking') stats.kLooking++;
        if (strikeoutType === 'swinging') stats.kSwinging++;
        break;
      case 'd3k_out':
        stats.d3k_out++;
        stats.k++;
        if (strikeoutType === 'looking') stats.kLooking++;
        if (strikeoutType === 'swinging') stats.kSwinging++;
        break;
      case 'bb':
        stats.bb++;
        stats.rbi += rbi;
        countsAsAB = false;
        break;
      case 'ibb':
        stats.ibb++;
        stats.bb++;
        countsAsAB = false;
        break;
      case 'hbp':
        stats.hbp++;
        stats.rbi += rbi;
        countsAsAB = false;
        break;
      case 'ci':
        stats.ci++;
        countsAsAB = false;
        break;
      case 'sf':
        stats.sf++;
        stats.rbi += rbi;
        if (risp) stats.rbi_risp += rbi;
        countsAsAB = false;
        break;
      case 'sh':
        stats.sh++;
        countsAsAB = false;
        break;
      case 'roe':
        stats.roe++;
        stats.rbi += rbi;
        break;
      case 'fc':
        stats.fc++;
        stats.rbi += rbi;
        break;
      default:
        break;
    }

    if (countsAsAB) {
      stats.ab++;
      if (risp) stats.ab_risp++;
    }
  }

  return stats;
}

function recalculateFieldingStats(existingPositions, actions) {
  const positionMeta = {};
  for (const [pos, data] of Object.entries(existingPositions)) {
    positionMeta[pos] = {
      innings: data.innings || 0,
      entryInning: data.entryInning ?? null,
      entryOuts: data.entryOuts ?? null,
      exitInning: data.exitInning ?? null,
      exitOuts: data.exitOuts ?? null,
    };
  }

  const positions = {};

  for (const action of actions) {
    const { position, type } = action;
    if (!positions[position]) {
      const meta = positionMeta[position] || {};
      positions[position] = {
        po: 0,
        assists: 0,
        errors: 0,
        throwingErrors: 0,
        fieldingErrors: 0,
        dp: 0,
        passedBalls: 0,
        scoredOnPassedBall: 0,
        sbAllowed: 0,
        caughtStealing: 0,
        blockedBalls: 0,
        droppedThird: 0,
        pickoffCS: 0,
        scoops: 0,
        stealAttempts: {
          second: { attempts: 0, cs: 0, sbAllowed: 0, errors: 0 },
          third:  { attempts: 0, cs: 0, sbAllowed: 0, errors: 0 },
        },
        pickoffAttempts: {
          first:  { attempts: 0, pickoffs: 0, errors: 0 },
          second: { attempts: 0, pickoffs: 0, errors: 0 },
          third:  { attempts: 0, pickoffs: 0, errors: 0 },
        },
        stealHome: { attempts: 0, stolen: 0 },
        innings: meta.innings || 0,
        entryInning: meta.entryInning ?? null,
        entryOuts: meta.entryOuts ?? null,
        exitInning: meta.exitInning ?? null,
        exitOuts: meta.exitOuts ?? null,
      };
    }
    const p = positions[position];
    if (type === 'putout') {
      p.po++;
      if (action.isScoop) p.scoops = (p.scoops || 0) + 1;
    }
    if (type === 'assist') p.assists++;
    if (type === 'error') {
      p.errors++;
      if (action.errorType === 'throwing') p.throwingErrors++;
      if (action.errorType === 'fielding') p.fieldingErrors++;
    }
    if (type === 'dp') {
      p.dp++;
      if (action.situation === 'dp_2nd_out' || action.situation === 'tp_3rd_out') p.po++;
      if (action.situation === 'dp_throw'   || action.situation === 'tp_throw')   p.assists++;
    }
    if (type === 'tp') {
      p.dp++;
      if (action.situation === 'tp_3rd_out') p.po++;
      if (action.situation === 'tp_throw')   p.assists++;
    }
    if (type === 'passedBall') p.passedBalls++;
    if (type === 'sbAllowed') p.sbAllowed++;
    if (type === 'caughtStealing') p.caughtStealing++;
    if (type === 'blockedBall') p.blockedBalls = (p.blockedBalls || 0) + 1;
    if (type === 'pickoffCS') p.pickoffCS = (p.pickoffCS || 0) + 1;
    if (type === 'strikeout') p.po++;

    if (type === 'throwAttempt') {
      const base = action.base; // 'second' | 'third'
      if (p.stealAttempts && p.stealAttempts[base]) {
        p.stealAttempts[base].attempts++;
        if (action.result === 'cs') {
          p.stealAttempts[base].cs++;
          p.caughtStealing++;
        }
        if (action.result === 'sbAllowed') {
          p.stealAttempts[base].sbAllowed++;
          p.sbAllowed++;
        }
        if (action.result === 'error') {
          p.stealAttempts[base].errors++;
          if (action.errorOn === 'catcher') {
            p.errors++;
            if (action.errorType === 'throwing') p.throwingErrors++;
            if (action.errorType === 'fielding') p.fieldingErrors++;
          }
        }
      }
    }

    if (type === 'pickoffAttempt') {
      const poBase = action.base || 'first';
      if (p.pickoffAttempts && p.pickoffAttempts[poBase]) {
        p.pickoffAttempts[poBase].attempts++;
        if (action.result === 'pickoff') {
          p.pickoffAttempts[poBase].pickoffs++;
          p.pickoffCS = (p.pickoffCS || 0) + 1;
          p.po++;
        }
        if (action.result === 'error') {
          p.pickoffAttempts[poBase].errors++;
          if (action.errorOn === 'catcher') {
            p.errors++;
            if (action.errorType === 'throwing') p.throwingErrors++;
            if (action.errorType === 'fielding') p.fieldingErrors++;
          }
        }
      }
    }

    if (type === 'droppedThird') {
      p.droppedThird = (p.droppedThird || 0) + 1;
      if (action.result === 'tagged') p.po++;
      if (action.result === 'threwOut') p.assists++;
      if (action.result === 'safe' && action.errorOn === 'catcher') {
        p.errors++;
        p.fieldingErrors++;
      }
    }

    if (type === 'stealHome') {
      if (p.stealHome) {
        p.stealHome.attempts++;
        if (action.how === 'steal') p.stealHome.stolen++;
        if (action.how === 'passedBall') {
          p.passedBalls++;
          p.scoredOnPassedBall = (p.scoredOnPassedBall || 0) + 1;
        }
        // wildPitch: nothing tallied for catcher
      }
    }
  }

  for (const [pos, meta] of Object.entries(positionMeta)) {
    if (!positions[pos]) {
      positions[pos] = {
        po: 0,
        assists: 0,
        errors: 0,
        throwingErrors: 0,
        fieldingErrors: 0,
        dp: 0,
        passedBalls: 0,
        scoredOnPassedBall: 0,
        sbAllowed: 0,
        caughtStealing: 0,
        blockedBalls: 0,
        droppedThird: 0,
        pickoffCS: 0,
        scoops: 0,
        stealAttempts: {
          second: { attempts: 0, cs: 0, sbAllowed: 0, errors: 0 },
          third:  { attempts: 0, cs: 0, sbAllowed: 0, errors: 0 },
        },
        pickoffAttempts: {
          first:  { attempts: 0, pickoffs: 0, errors: 0 },
          second: { attempts: 0, pickoffs: 0, errors: 0 },
          third:  { attempts: 0, pickoffs: 0, errors: 0 },
        },
        stealHome: { attempts: 0, stolen: 0 },
        innings: meta.innings || 0,
        entryInning: meta.entryInning ?? null,
        entryOuts: meta.entryOuts ?? null,
        exitInning: meta.exitInning ?? null,
        exitOuts: meta.exitOuts ?? null,
      };
    }
  }

  return positions;
}

// ─────────────────────────────────────────────
// REDUCER
// ─────────────────────────────────────────────

const PA_ENDING = [
  'k',
  'bb',
  'ibb',
  'hbp',
  'ci',
  'gidp',
  'd3k_reached',
  'd3k_out',
  'single',
  'double',
  'triple',
  'hr',
  'iphr',
  'out',
  'roe',
  'fc',
  'sh',
  'sf',
];

function reducer(state, action) {
  switch (action.type) {
    case 'SET_PLAYER':
      return {
        ...state,
        playerId: action.playerId,
        playerName: action.playerName,
        playerIsPitcher: action.playerIsPitcher ?? false,
        playerIsPitcherOnly: action.playerIsPitcherOnly ?? false,
        playerPitches: action.playerPitches ?? [],
      };
    case 'SET_GAME_INFO':
      return { ...state, [action.field]: action.value };
    case 'SET_TRACKING':
      return {
        ...state,
        tracking: { ...state.tracking, [action.key]: action.value },
      };
    case 'LOAD_STATE':
      return action.state;
    case 'RESET_ALL':
      localStorage.removeItem('pmk_tracker_game');
      return {
        ...INITIAL_STATE,
        gameDate: new Date().toISOString().split('T')[0],
      };
    case 'RESTORE_LIVE_STATE':
      return { ...action.liveState };

    // ── Pitching setup ────────────────────────
    case 'RESET_PITCHING':
      return {
        ...state,
        pitching: { ...INITIAL_PITCHING },
      };
    case 'START_PITCHING': {
      const currentInning = state.pitching.currentInning || 1;
      const currentOuts = state.pitching.stats?.outs % 3 || 0;
      const positions = { ...state.fielding.positions };
      if (!positions['P']) {
        positions['P'] = {
          po: 0,
          assists: 0,
          errors: 0,
          throwingErrors: 0,
          fieldingErrors: 0,
          dp: 0,
          passedBalls: 0,
          sbAllowed: 0,
          caughtStealing: 0,
          innings: 0,
          entryInning: currentInning,
          entryOuts: currentOuts,
          exitInning: null,
          exitOuts: null,
        };
      }
      return {
        ...state,
        pitching: {
          ...state.pitching,
          pitchingStarted: true,
        },
        fielding: {
          ...state.fielding,
          positions,
          activePosition: 'P',
        },
      };
    }
    case 'SET_PITCHING_ROLE': {
      const reliefInningNum = parseFloat(action.reliefInning) || 0;
      const startInning = action.role === 'Relief' && reliefInningNum > 0
        ? Math.floor(reliefInningNum)
        : state.pitching.currentInning;
      const reliefEntryOuts = action.role === 'Relief' && reliefInningNum > 0
        ? Math.round((reliefInningNum % 1) * 10)
        : 0;
      // Auto-populate diamond from inheritedBases
      const ib = action.inheritedBases || [];
      const baseMap = { '1B': 'first', '2B': 'second', '3B': 'third' };
      const newOpponentBaseState = { first: null, second: null, third: null };
      if (ib.length > 0) {
        ib.forEach((b) => {
          if (baseMap[b]) {
            newOpponentBaseState[baseMap[b]] = { reachedVia: 'inherited', isEarned: false };
          }
        });
      }
      // Update P position entry inning/outs when relief inning is set
      let updatedFielding = state.fielding;
      if (action.role === 'Relief' && reliefInningNum > 0 && state.fielding.positions['P']) {
        updatedFielding = {
          ...state.fielding,
          positions: {
            ...state.fielding.positions,
            P: {
              ...state.fielding.positions['P'],
              entryInning: startInning,
              entryOuts: reliefEntryOuts,
            },
          },
        };
      }
      return {
        ...state,
        pitching: {
          ...state.pitching,
          role: action.role,
          reliefInning: action.reliefInning || '',
          inheritedRunners: action.inheritedRunners || 0,
          inheritedBases: ib,
          currentInning: startInning,
          opponentBaseState: ib.length > 0 ? newOpponentBaseState : state.pitching.opponentBaseState,
        },
        fielding: updatedFielding,
      };
    }

    case 'SET_PITCHING_DECISION':
      return {
        ...state,
        pitching: { ...state.pitching, decision: action.decision },
      };
    case 'DONE_PITCHING':
      return { ...state, pitching: { ...state.pitching, pitchingDone: true } };
    case 'REACTIVATE_PITCHING':
      return { ...state, pitching: { ...state.pitching, pitchingDone: false } };

    case 'SET_CURRENT_INNING':
      return {
        ...state,
        pitching: {
          ...state.pitching,
          currentInning: action.inning,
          pendingInningEnd: false,
          phantomOuts: 0,
          opponentBaseState: { first: null, second: null, third: null },
          inningPoisoned: false,
        },
      };
    case 'SET_OPPONENT_BASE': {
      const current = state.pitching.opponentBaseState;
      const updated = { ...current };
      for (const [base, val] of Object.entries(action.bases)) {
        if (val === null || val === false) {
          // Clear the base
          updated[base] = null;
        } else if (typeof val === 'object') {
          // Full Runner object: { reachedVia, isEarned, ... }
          updated[base] = {
            reachedVia:  val.reachedVia  ?? 'hit',
            isEarned:    val.isEarned    ?? true,
          };
        }
      }
      return {
        ...state,
        pitching: {
          ...state.pitching,
          opponentBaseState: updated,
        },
      };
    }
    case 'CLEAR_OPPONENT_BASES':
      return {
        ...state,
        pitching: {
          ...state.pitching,
          opponentBaseState: { first: null, second: null, third: null },
        },
      };
    case 'SET_INNING_POISONED':
      return {
        ...state,
        pitching: {
          ...state.pitching,
          inningPoisoned: action.value,
        },
      };
    case 'SET_PHANTOM_OUTS':
      return {
        ...state,
        pitching: {
          ...state.pitching,
          phantomOuts: action.value,
        },
      };

    case 'CLEAR_PENDING_INNING_END':
      return {
        ...state,
        pitching: { ...state.pitching, pendingInningEnd: false },
      };

    case 'INHERITED_RUNNER_SCORED': {
      const capped = Math.min(
        state.pitching.inheritedRunnersScored + 1,
        state.pitching.inheritedRunners
      );
      return {
        ...state,
        pitching: { ...state.pitching, inheritedRunnersScored: capped },
      };
    }

    // ── Pitching actions ──────────────────────
    case 'ADD_PITCH_ACTION': {
      const newActions = [
        ...state.pitching.actions,
        {
          id: generateId(),
          inning: state.pitching.currentInning,
          ...action.payload,
          timestamp: new Date().toISOString(),
          snapshot: {
            opponentBaseState: { ...state.pitching.opponentBaseState },
            phantomOuts: state.pitching.phantomOuts,
            inningPoisoned: state.pitching.inningPoisoned,
            currentInning: state.pitching.currentInning,
            runs: state.pitching.stats.runs,
            earnedRuns: state.pitching.stats.earnedRuns,
          },
        },
      ];
      const { strikes, balls } = recalculateCount(newActions);
      const { stats, velocities, inningBreakdowns } = recalculatePitchingStats(
        newActions,
        state.pitching.currentInning
      );
      const reliefPartialOuts = state.pitching.role === 'Relief' && state.pitching.currentInning === Math.floor(parseFloat(state.pitching.reliefInning || 0))
        ? Math.round((parseFloat(state.pitching.reliefInning || 0) % 1) * 10)
        : 0;
      const currentInningOuts = (inningBreakdowns.find(b => b.inning === state.pitching.currentInning)?.outs || 0);
      const inningComplete = (currentInningOuts + reliefPartialOuts) >= 3;
      return {
        ...state,
        pitching: {
          ...state.pitching,
          actions: newActions,
          strikeCounter: strikes,
          ballCounter: balls,
          stats,
          velocities,
          inningBreakdowns,
          pendingInningEnd: inningComplete,
        },
      };
    }

    case 'UPDATE_PITCH_ACTION': {
      const newActions = state.pitching.actions.map((a) =>
        a.id === action.id ? { ...a, ...action.changes } : a
      );
      const { strikes, balls } = recalculateCount(newActions);
      const { stats, velocities, inningBreakdowns } = recalculatePitchingStats(
        newActions,
        state.pitching.currentInning
      );
      return {
        ...state,
        pitching: {
          ...state.pitching,
          actions: newActions,
          strikeCounter: strikes,
          ballCounter: balls,
          stats,
          velocities,
          inningBreakdowns,
        },
      };
    }

    case 'DELETE_PITCH_ACTION': {
      const newActions = state.pitching.actions.filter(
        (a) => a.id !== action.id
      );
      const { strikes, balls } = recalculateCount(newActions);
      const { stats, velocities, inningBreakdowns } = recalculatePitchingStats(
        newActions,
        state.pitching.currentInning
      );
      return {
        ...state,
        pitching: {
          ...state.pitching,
          actions: newActions,
          strikeCounter: strikes,
          ballCounter: balls,
          stats,
          velocities,
          inningBreakdowns,
        },
      };
    }

    case 'UNDO_LAST_PITCH': {
      if (!state.pitching.actions.length) return state;
      const lastAction = state.pitching.actions[state.pitching.actions.length - 1];
      const newActions = state.pitching.actions.slice(0, -1);
      const snap = lastAction.snapshot;
      const { strikes, balls } = recalculateCount(newActions);
      const inningForRecalc = snap?.currentInning ?? state.pitching.currentInning;
      const { stats, velocities, inningBreakdowns } = recalculatePitchingStats(
        newActions,
        inningForRecalc
      );
      return {
        ...state,
        pitching: {
          ...state.pitching,
          actions: newActions,
          strikeCounter: strikes,
          ballCounter: balls,
          stats,
          velocities,
          inningBreakdowns,
          opponentBaseState: snap?.opponentBaseState ?? state.pitching.opponentBaseState,
          phantomOuts: snap?.phantomOuts ?? state.pitching.phantomOuts,
          inningPoisoned: snap?.inningPoisoned ?? state.pitching.inningPoisoned,
          currentInning: snap?.currentInning ?? state.pitching.currentInning,
          pendingInningEnd: false,
        },
      };
    }

    case 'SET_LAST_PITCH_VELOCITY': {
      if (!state.pitching.actions.length) return state;
      const actions = [...state.pitching.actions];
      actions[actions.length - 1] = {
        ...actions[actions.length - 1],
        velocity: action.velocity,
      };
      const { stats, velocities, inningBreakdowns } = recalculatePitchingStats(
        actions,
        state.pitching.currentInning
      );
      return {
        ...state,
        pitching: {
          ...state.pitching,
          actions,
          stats,
          velocities,
          inningBreakdowns,
        },
      };
    }

    // ── R/ER manual adjustments ───────────────
    case 'ADJUST_PITCHING_RUNS': {
      const newActions = [
        ...state.pitching.actions,
        {
          id: generateId(),
          type: 'adjustRuns',
          value: action.value,
          timestamp: new Date().toISOString(),
        },
      ];
      const { stats, velocities, inningBreakdowns } = recalculatePitchingStats(
        newActions,
        state.pitching.currentInning
      );
      return {
        ...state,
        pitching: {
          ...state.pitching,
          actions: newActions,
          stats,
          velocities,
          inningBreakdowns,
        },
      };
    }

    case 'ADJUST_PITCHING_EARNED_RUNS': {
      const newActions = [
        ...state.pitching.actions,
        {
          id: generateId(),
          type: 'adjustEarnedRuns',
          value: action.value,
          timestamp: new Date().toISOString(),
        },
      ];
      const { stats, velocities, inningBreakdowns } = recalculatePitchingStats(
        newActions,
        state.pitching.currentInning
      );
      return {
        ...state,
        pitching: {
          ...state.pitching,
          actions: newActions,
          stats,
          velocities,
          inningBreakdowns,
        },
      };
    }

    // ── Batting setup ─────────────────────────
    case 'SET_BATTING_STATUS':
      return {
        ...state,
        batting: {
          ...state.batting,
          status: action.status,
          substituteInning: action.substituteInning || '',
        },
      };

    // "New At Bat" — parent sets base state, tracking prefs carry forward
    case 'START_AT_BAT':
      return {
        ...state,
        batting: {
          ...state.batting,
          atBatPhase: 'active',
          currentAtBatId: generateId(),
          currentAtBat: {
            baseState: action.baseState || INITIAL_BASE_STATE,
            risp: hasRISP(action.baseState || INITIAL_BASE_STATE),
            strikeCounter: 0,
            ballCounter: 0,
          },
          trackingPrefs: action.trackingPrefs || state.batting.trackingPrefs,
        },
      };

    case 'UPDATE_AT_BAT_COUNT': {
      const cur = state.batting.currentAtBat;
      return {
        ...state,
        batting: {
          ...state.batting,
          currentAtBat: {
            ...cur,
            strikeCounter: action.strikes ?? cur.strikeCounter,
            ballCounter: action.balls ?? cur.ballCounter,
            baseState: action.baseState ?? cur.baseState,
            risp: action.baseState ? hasRISP(action.baseState) : cur.risp,
          },
        },
      };
    }

    case 'RESET_AT_BAT':
      return {
        ...state,
        batting: {
          ...state.batting,
          atBatPhase: 'idle',
          currentAtBat: {
            baseState: INITIAL_BASE_STATE,
            risp: false,
            strikeCounter: 0,
            ballCounter: 0,
          },
        },
      };

    // ── Batting actions ───────────────────────
    case 'ADD_COUNT_ACTION': {
      const cur = state.batting.currentAtBat;
      const newAction = {
        id: generateId(),
        atBatId: state.batting.currentAtBatId,
        outcome: action.outcome,
        timestamp: new Date().toISOString(),
        snapshot: {
          atBatPhase: state.batting.atBatPhase,
          currentAtBat: { ...cur },
          pitchCounts: action.pitchCounts || { swinging: 0, looking: 0, fouls: 0, balls: 0 },
          playerPosition: state.batting.playerPosition ?? null,
        },
      };
      return {
        ...state,
        batting: {
          ...state.batting,
          actions: [...state.batting.actions, newAction],
          currentAtBat: {
            ...cur,
            strikeCounter: action.strikeCounter ?? cur.strikeCounter,
            ballCounter: action.ballCounter ?? cur.ballCounter,
          },
        },
      };
    }
    case 'ADD_BATTING_ACTION': {
      const newAction = {
        id: generateId(),
        atBatId: state.batting.currentAtBatId,
        risp: state.batting.currentAtBat.risp,
        baseState: state.batting.currentAtBat.baseState,
        ...action.payload,
        timestamp: new Date().toISOString(),
        snapshot: {
          atBatPhase: state.batting.atBatPhase,
          currentAtBat: { ...state.batting.currentAtBat },
          pitchCounts: action.payload.pitchCounts || { swinging: 0, looking: 0, fouls: 0, balls: 0 },
          playerPosition: action.payload.playerPosition ?? null,
        },
      };
      const newActions = [...state.batting.actions, newAction];
      const stats = recalculateBattingStats(newActions);
      const endsAtBat = PA_ENDING.includes(action.payload.outcome);
      const currentPhase = state.batting.atBatPhase;
      const newPhase =
        currentPhase === 'active' ? (endsAtBat ? 'idle' : 'active') : 'idle';
      return {
        ...state,
        batting: {
          ...state.batting,
          actions: newActions,
          stats,
          atBatPhase: newPhase,
          currentAtBatId: endsAtBat ? null : state.batting.currentAtBatId,
          playerPosition: action.payload.playerPosition ?? state.batting.playerPosition,
          currentAtBat: endsAtBat
            ? {
                baseState: INITIAL_BASE_STATE,
                risp: false,
                strikeCounter: 0,
                ballCounter: 0,
              }
            : state.batting.currentAtBat,
        },
      };
    }

    case 'UPDATE_BATTING_ACTION': {
      const newActions = state.batting.actions.map((a) =>
        a.id === action.id ? { ...a, ...action.changes } : a
      );
      return {
        ...state,
        batting: {
          ...state.batting,
          actions: newActions,
          stats: recalculateBattingStats(newActions),
        },
      };
    }

    case 'UNDO_LAST_COMPLETED_AT_BAT': {
      // Find the last PA-ending action
      const lastPA = [...state.batting.actions].reverse().find(a => PA_ENDING.includes(a.outcome));
      if (!lastPA) return state;
      const atBatId = lastPA.atBatId;
      // Only remove the PA-ending action — leave count actions in place for subsequent undos
      const newActions = state.batting.actions.filter(a => a.id !== lastPA.id);
      const stats = recalculateBattingStats(newActions);
      // Use last count action's snapshot for currentAtBat — has correct pre-PA counters
      const atBatCountActions = state.batting.actions.filter(a => a.atBatId === atBatId && (a.outcome === 'count_ball' || a.outcome === 'count_strike'));
      const lastCountSnap = atBatCountActions.length > 0 ? atBatCountActions[atBatCountActions.length - 1].snapshot : null;
      const currentAtBatToRestore = lastCountSnap?.currentAtBat ?? lastPA.snapshot?.currentAtBat ?? {
        baseState: INITIAL_BASE_STATE,
        risp: false,
        strikeCounter: 0,
        ballCounter: 0,
      };
      return {
        ...state,
        batting: {
          ...state.batting,
          actions: newActions,
          stats,
          atBatPhase: 'active',
          currentAtBatId: atBatId,
          currentAtBat: currentAtBatToRestore,
          playerPosition: lastPA.snapshot?.playerPosition ?? null,
        },
      };
    }
    case 'UNDO_LAST_BATTING_ACTION': {
      const lastAction = state.batting.actions[state.batting.actions.length - 1];
      // Only undo actions belonging to the current at-bat
      if (!lastAction || state.batting.currentAtBatId === null || lastAction.atBatId !== state.batting.currentAtBatId) {
        return state;
      }
      const newActions = state.batting.actions.slice(0, -1);
      const snap = lastAction.snapshot;
      const stats = recalculateBattingStats(newActions);
      return {
        ...state,
        batting: {
          ...state.batting,
          actions: newActions,
          stats,
          atBatPhase: snap?.atBatPhase ?? 'idle',
          playerPosition: snap?.playerPosition ?? null,
          currentAtBat: snap?.currentAtBat ?? {
            baseState: INITIAL_BASE_STATE,
            risp: false,
            strikeCounter: 0,
            ballCounter: 0,
          },
        },
      };
    }
    case 'DELETE_BATTING_ACTION': {
      const newActions = state.batting.actions.filter(
        (a) => a.id !== action.id
      );
      return {
        ...state,
        batting: {
          ...state.batting,
          actions: newActions,
          stats: recalculateBattingStats(newActions),
        },
      };
    }
    case 'DELETE_BATTING_AT_BAT': {
      const newActions = state.batting.actions.filter(
        (a) => String(a.atBatId) !== String(action.atBatId)
      );
      return {
        ...state,
        batting: {
          ...state.batting,
          actions: newActions,
          stats: recalculateBattingStats(newActions),
        },
      };
    }

    case 'SET_BATTING_TRACKING_PREFS':
      return {
        ...state,
        batting: {
          ...state.batting,
          trackingPrefs: { ...state.batting.trackingPrefs, ...action.prefs },
        },
      };

    // ── Fielding ──────────────────────────────
    case 'SET_ACTIVE_POSITION': {
      const positions = { ...state.fielding.positions };
      if (!positions[action.position]) {
        positions[action.position] = {
          po: 0,
          assists: 0,
          errors: 0,
          throwingErrors: 0,
          fieldingErrors: 0,
          dp: 0,
          passedBalls: 0,
          sbAllowed: 0,
          caughtStealing: 0,
          innings: 0,
          entryInning: action.entryInning || null,
          entryOuts: action.entryOuts ?? null,
          exitInning: null,
          exitOuts: null,
        };
      }
      return {
        ...state,
        fielding: {
          ...state.fielding,
          positions,
          activePosition: action.position,
        },
      };
    }

    case 'SET_POSITION_ENTRY': {
      const positions = {
        ...state.fielding.positions,
        [action.position]: {
          ...state.fielding.positions[action.position],
          entryInning: action.entryInning,
          entryOuts: action.entryOuts,
        },
      };
      return { ...state, fielding: { ...state.fielding, positions } };
    }
    case 'CLEAR_POSITION_EXIT': {
      const pos = state.fielding.positions[action.position];
      if (!pos) return state;
      const positions = {
        ...state.fielding.positions,
        [action.position]: { ...pos, exitInning: null, exitOuts: null, innings: null },
      };
      return { ...state, fielding: { ...state.fielding, positions } };
    }
    case 'SET_POSITION_EXIT': {
      const pos = state.fielding.positions[action.position];
      const entryInning = pos?.entryInning || 1;
      const entryOuts = pos?.entryOuts ?? 0;
      const exitInning = action.exitInning;
      const exitOuts = action.exitOuts;
      // Calculate innings in baseball notation (X.Y where Y is 0, 1, or 2)
      const totalEntryOuts = ((entryInning - 1) * 3) + entryOuts;
      const totalExitOuts = ((exitInning - 1) * 3) + exitOuts;
      const totalOuts = Math.max(0, totalExitOuts - totalEntryOuts);
      const fullInnings = Math.floor(totalOuts / 3);
      const remainderOuts = totalOuts % 3;
      const innings = parseFloat(`${fullInnings}.${remainderOuts}`);
      const positions = {
        ...state.fielding.positions,
        [action.position]: {
          ...pos,
          exitInning,
          exitOuts,
          innings,
        },
      };
      const pitchingUpdate = action.position === 'P' && state.pitching.pitchingStarted && !state.pitching.pitchingDone
        ? { pitching: { ...state.pitching, pitchingDone: true } }
        : {};
      return { ...state, fielding: { ...state.fielding, positions }, ...pitchingUpdate };
    }
    case 'UPDATE_POSITION_INNINGS': {
      const positions = {
        ...state.fielding.positions,
        [action.position]: {
          ...state.fielding.positions[action.position],
          innings: action.innings,
        },
      };
      return { ...state, fielding: { ...state.fielding, positions } };
    }

    case 'ADD_FIELDING_ACTION': {
      const newActions = [
        ...state.fielding.actions,
        {
          id: generateId(),
          ...action.payload,
          timestamp: new Date().toISOString(),
        },
      ];
      return {
        ...state,
        fielding: {
          ...state.fielding,
          actions: newActions,
          positions: recalculateFieldingStats(
            state.fielding.positions,
            newActions
          ),
        },
      };
    }

    case 'UPDATE_FIELDING_ACTION': {
      const newActions = state.fielding.actions.map((a) =>
        a.id === action.id ? { ...a, ...action.changes } : a
      );
      return {
        ...state,
        fielding: {
          ...state.fielding,
          actions: newActions,
          positions: recalculateFieldingStats(
            state.fielding.positions,
            newActions
          ),
        },
      };
    }

    case 'DELETE_FIELDING_ACTION': {
      const newActions = state.fielding.actions.filter(
        (a) => a.id !== action.id
      );
      return {
        ...state,
        fielding: {
          ...state.fielding,
          actions: newActions,
          positions: recalculateFieldingStats(
            state.fielding.positions,
            newActions
          ),
        },
      };
    }

    case 'DELETE_FIELDING_POSITION': {
      const positions = { ...state.fielding.positions };
      delete positions[action.position];
      const newActions = state.fielding.actions.filter(
        (a) => a.position !== action.position
      );
      return {
        ...state,
        fielding: {
          ...state.fielding,
          positions,
          actions: newActions,
          activePosition:
            state.fielding.activePosition === action.position
              ? null
              : state.fielding.activePosition,
        },
      };
    }

    default:
      return state;
  }
}

// ─────────────────────────────────────────────
// SELECTORS
// ─────────────────────────────────────────────

export function selectPitchingIP(state) {
  const outs = state.pitching.stats.outs;
  return parseFloat(`${Math.floor(outs / 3)}.${outs % 3}`);
}

export function selectBattingRatios(state) {
  const s = state.batting.stats;
  const avg = s.ab > 0 ? s.h / s.ab : 0;
  const obpD = s.ab + s.bb + s.hbp + s.sf;
  const obp = obpD > 0 ? (s.h + s.bb + s.hbp) / obpD : 0;
  const tb = s.singles + 2 * s.doubles + 3 * s.triples + 4 * s.hr;
  const slg = s.ab > 0 ? tb / s.ab : 0;
  const ops = obp + slg;
  const babipD = s.ab - s.k - s.hr + s.sf;
  const babip = babipD > 0 ? (s.h - s.hr) / babipD : 0;
  const iso = s.ab > 0 ? (tb - s.h) / s.ab : 0;
  return { avg, obp, slg, ops, babip, iso, tb };
}

export function selectFieldingTotals(state) {
  let po = 0,
    assists = 0,
    errors = 0,
    dp = 0,
    passedBalls = 0,
    scoredOnPassedBall = 0,
    sbAllowed = 0,
    caughtStealing = 0;
  for (const s of Object.values(state.fielding.positions)) {
    po += s.po;
    assists += s.assists;
    errors += s.errors;
    dp += s.dp;
    passedBalls += s.passedBalls || 0;
    scoredOnPassedBall += s.scoredOnPassedBall || 0;
    sbAllowed += s.sbAllowed || 0;
    caughtStealing += s.caughtStealing || 0;
  }
  const tc = po + assists + errors;
  const fpct = tc > 0 ? (po + assists) / tc : 0;
  const csPct =
    sbAllowed + caughtStealing > 0
      ? Math.round((caughtStealing / (sbAllowed + caughtStealing)) * 100)
      : 0;
  return {
    po,
    assists,
    errors,
    dp,
    tc,
    fpct,
    passedBalls,
    scoredOnPassedBall,
    sbAllowed,
    caughtStealing,
    csPct,
  };
}

export function selectPitchTypeStats(state) {
  const pitchTypes = {};
  let totalPitches = 0,
    totalStrikes = 0;

  for (const action of state.pitching.actions) {
    if (!action.pitchType) continue;
    if (!['strike', 'ball', 'ballInPlay', 'wildPitch'].includes(action.type))
      continue;

    if (!pitchTypes[action.pitchType]) {
      pitchTypes[action.pitchType] = {
        total: 0,
        strikes: 0,
        balls: 0,
        swinging: 0,
        looking: 0,
        foul: 0,
        foulTip: 0,
        ballInPlay: 0,
      };
    }
    const p = pitchTypes[action.pitchType];
    p.total++;
    totalPitches++;

    if (action.type === 'strike') {
      p.strikes++;
      totalStrikes++;
      if (action.detail === 'swinging') p.swinging++;
      if (action.detail === 'looking') p.looking++;
      if (action.detail === 'foul') p.foul++;
      if (action.detail === 'foulTip') p.foulTip++;
    } else if (action.type === 'ballInPlay') {
      p.strikes++;
      p.ballInPlay++;
      totalStrikes++;
    } else {
      p.balls++;
    }
  }

  return {
    pitchTypes,
    totalPitches,
    totalStrikes,
    overallStrikePct:
      totalPitches > 0 ? Math.round((totalStrikes / totalPitches) * 100) : 0,
  };
}

/**
 * Command trend & fatigue analysis.
 * Derived from per-inning breakdowns — feeds smart insights on the analytics dashboard.
 */
export function selectCommandTrend(state) {
  const bd = state.pitching.inningBreakdowns;
  if (bd.length < 2)
    return { trend: 'insufficient_data', alerts: [], fpsPct: null };

  const alerts = [];

  const recent = bd.slice(-3);
  const earlier = bd.slice(0, Math.max(1, bd.length - 3));
  const recentBallPct =
    recent.reduce((s, d) => s + (100 - d.strikePct), 0) / recent.length;
  const earlierBallPct =
    earlier.reduce((s, d) => s + (100 - d.strikePct), 0) / earlier.length;
  const diff = recentBallPct - earlierBallPct;

  if (diff >= 15) {
    alerts.push({
      type: 'fatigue_warning',
      severity: 'high',
      message: `Ball% up ${Math.round(diff)}% over recent innings — possible fatigue. Monitor arm carefully.`,
    });
  } else if (diff >= 8) {
    alerts.push({
      type: 'command_declining',
      severity: 'medium',
      message: `Command trending off — ball% up ${Math.round(diff)}% in recent innings.`,
    });
  }

  const total = state.pitching.stats.pitchCount;
  if (total >= 105)
    alerts.push({
      type: 'pitch_count',
      severity: 'high',
      message: `Over 105 pitches (${total}). High fatigue risk — consider rest.`,
    });
  else if (total >= 95)
    alerts.push({
      type: 'pitch_count',
      severity: 'medium',
      message: `Approaching 100 pitches (${total}). Monitor arm health.`,
    });

  const fps = state.pitching.stats.firstPitchStrikes;
  const fpb = state.pitching.stats.firstPitchBalls;
  const fpT = fps + fpb;
  const fpsPct = fpT > 0 ? Math.round((fps / fpT) * 100) : null;
  if (fpsPct !== null && fpsPct < 50 && fpT >= 5) {
    alerts.push({
      type: 'first_pitch_command',
      severity: 'medium',
      message: `First-pitch strike rate ${fpsPct}% — batters are consistently ahead in the count.`,
    });
  }

  let trend = 'stable';
  if (diff >= 15) trend = 'declining_significantly';
  else if (diff >= 8) trend = 'declining';
  else if (diff <= -8) trend = 'improving';

  return {
    trend,
    alerts,
    fpsPct,
    recentBallPct: Math.round(recentBallPct),
    earlierBallPct: Math.round(earlierBallPct),
  };
}

// ─────────────────────────────────────────────
// BUILD API PAYLOAD
// ─────────────────────────────────────────────

export function buildGamePayload(state) {
  const payload = {
    gameDate: state.gameDate,
    opponent: state.opponent,
    sport: state.sport,
    homeAway: state.homeAway,
    gameType: state.gameType,
    teamScore: state.teamScore,
    opponentScore: state.opponentScore,
    venue: state.venue || null,
    notes: state.notes || null,
  };

  if (state.pitching.actions.length > 0) {
    const s = state.pitching.stats;
    const ip = selectPitchingIP(state);
    const vel = state.pitching.velocities;

    const pitching = {
      role: state.pitching.role,
      decision: state.pitching.decision,
      ip,
      h: s.hits,
      hr: s.hr,
      r: s.runs,
      er: s.earnedRuns,
      bb: s.walks,
      ibb: s.ibb,
      hbp: s.hbp,
      k: s.strikeouts,
      pitches: s.pitchCount,
      first_pitch_strikes: s.firstPitchStrikes,
      first_pitch_balls: s.firstPitchBalls,
      wp: s.wildPitches,
      balks: s.balks,
      pickoffs: s.pickoffs,
      sb_allowed: s.sbAllowed || 0,
      cs_attempts: s.csAttempts || 0,
      cs_outs: s.csOuts || 0,
      avg_velo: vel.length
        ? Math.round(vel.reduce((a, b) => a + b, 0) / vel.length)
        : null,
      max_velo: vel.length ? Math.max(...vel) : null,
      inning_breakdowns: state.pitching.inningBreakdowns,
    };

    if (state.pitching.role === 'Relief') {
      pitching.relief_inning = parseFloat(state.pitching.reliefInning) || null;
      pitching.inherited_runners = state.pitching.inheritedRunners;
      pitching.inherited_bases = state.pitching.inheritedBases;
      pitching.inherited_runners_scored = state.pitching.inheritedRunnersScored;
    }

    if (vel.length) pitching.velocities = vel;

    const detailed = state.pitching.actions
      .filter((a) =>
        ['strike', 'ball', 'ballInPlay', 'wildPitch'].includes(a.type)
      )
      .map((a) => {
        const p = { type: a.pitchType || 'Unknown', outcome: a.type };
        if (a.detail) p.detail = a.detail;
        if (a.velocity) p.velocity = a.velocity;
        if (a.hitType) p.hitType = a.hitType;
        return p;
      });
    if (detailed.length) pitching.detailed_pitches = detailed;
    const pitchStats = selectPitchTypeStats(state);
    pitching.total_strikes = pitchStats.totalStrikes;
    pitching.total_pitches = pitchStats.totalPitches;
    pitching.strike_pct = pitchStats.overallStrikePct;
    if (Object.keys(pitchStats.pitchTypes).length > 0) {
      pitching.pitch_type_stats = pitchStats.pitchTypes;
    }
    payload.pitching = pitching;
  }

  if (state.batting.actions.length > 0) {
    const s = state.batting.stats;
    const { tb } = selectBattingRatios(state);

    const batting = {
      status: state.batting.status,
      pa: s.pa,
      ab: s.ab,
      h: s.h,
      r: s.runs,
      '1b': s.singles,
      '2b': s.doubles,
      '3b': s.triples,
      hr: s.hr,
      iphr: s.iphr,
      rbi: s.rbi,
      tb,
      bb: s.bb,
      ibb: s.ibb,
      hbp: s.hbp,
      k: s.k,
      k_looking: s.kLooking,
      k_swinging: s.kSwinging,
      sb: s.sb,
      cs: s.cs,
      pik: s.pickedOff,
      gidp: s.gidp,
      sf: s.sf,
      sh: s.sh,
      roe: s.roe,
      fc: s.fc,
      d3k_reached: s.d3k_reached,
      d3k_out: s.d3k_out,
      ci: s.ci,
      ab_risp: s.ab_risp,
      h_risp: s.h_risp,
      rbi_risp: s.rbi_risp,
    };

    if (
      state.batting.status === 'Substitute' &&
      state.batting.substituteInning
    ) {
      batting.substitute_inning = parseInt(state.batting.substituteInning);
    }

    const pas = state.batting.actions
      .filter((a) => !['run', 'sb', 'cs', 'pickedOff'].includes(a.outcome))
      .map((a) => {
        const pa = {
          outcome: a.outcome,
          risp: a.risp || false,
          baseState: a.baseState || null,
        };
        if (a.rbi) pa.rbi = a.rbi;
        if (a.hitType) pa.hitType = a.hitType;
        if (a.hitLocation) pa.hitLocation = a.hitLocation;
        if (a.strikeoutType) pa.strikeoutType = a.strikeoutType;
        if (a.outCount != null) pa.outCount = a.outCount;
        return pa;
      });
    if (pas.length) batting.plate_appearances = pas;

    payload.batting = batting;
  }

  if (Object.keys(state.fielding.positions).length > 0) {
    payload.fielding = Object.entries(state.fielding.positions).map(
      ([pos, s]) => ({
        position: pos,
        innings: s.innings,
        po: s.po,
        a: s.assists,
        e: s.errors,
        throwing_errors: s.throwingErrors,
        fielding_errors: s.fieldingErrors,
        tc: s.po + s.assists + s.errors,
        dp: s.dp,
        ...(pos === 'C' && {
          passed_balls: s.passedBalls,
          scored_on_passed_ball: s.scoredOnPassedBall || 0,
          sb_allowed: s.sbAllowed,
          caught_stealing: s.caughtStealing,
          blocked_balls: s.blockedBalls || 0,
          dropped_third: s.droppedThird || 0,
          pickoff_cs: s.pickoffCS || 0,
          steal_attempts: s.stealAttempts || null,
          pickoff_attempts: s.pickoffAttempts || null,
          steal_home: s.stealHome || null,
        }),
      })
    );
  }

  return payload;
}

// ─────────────────────────────────────────────
// THE HOOK
// ─────────────────────────────────────────────

const STORAGE_KEY = 'pmk_tracker_game';

export function useGameState() {
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE, (initial) => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : initial;
    } catch {
      return initial;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {}
  }, [state]);

  // Action creators
  const setPlayer = useCallback(
    (pid, name, isPitcher, isPitcherOnly, pitches) =>
      dispatch({
        type: 'SET_PLAYER',
        playerId: pid,
        playerName: name,
        playerIsPitcher: isPitcher ?? false,
        playerIsPitcherOnly: isPitcherOnly ?? false,
        playerPitches: pitches ?? [],
      }),
    []
  );
  const setGameInfo = useCallback(
    (field, value) => dispatch({ type: 'SET_GAME_INFO', field, value }),
    []
  );
  const setTracking = useCallback(
    (key, value) => dispatch({ type: 'SET_TRACKING', key, value }),
    []
  );
  const resetAll = useCallback(() => dispatch({ type: 'RESET_ALL' }), []);
  const restoreLiveState = useCallback(
    (liveState) => dispatch({ type: 'RESTORE_LIVE_STATE', liveState }),
    []
  );

  const startPitching = useCallback(
    () => dispatch({ type: 'START_PITCHING' }),
    []
  );
  const resetPitching = useCallback(
    () => dispatch({ type: 'RESET_PITCHING' }),
    []
  );
  const setPitchingRole = useCallback(
    (role, reliefInning, inheritedRunners, inheritedBases) =>
      dispatch({
        type: 'SET_PITCHING_ROLE',
        role,
        reliefInning,
        inheritedRunners,
        inheritedBases,
      }),
    []
  );
  const setPitchingDecision = useCallback(
    (decision) => dispatch({ type: 'SET_PITCHING_DECISION', decision }),
    []
  );
  const setOpponentBase = useCallback(
    (bases) => dispatch({ type: 'SET_OPPONENT_BASE', bases }),
    []
  );
  const clearOpponentBases = useCallback(
    () => dispatch({ type: 'CLEAR_OPPONENT_BASES' }),
    []
  );
  const setInningPoisoned = useCallback(
    (value) => dispatch({ type: 'SET_INNING_POISONED', value }),
    []
  );
  const setPhantomOuts = useCallback(
    (value) => dispatch({ type: 'SET_PHANTOM_OUTS', value }),
    []
  );
  const setCurrentInning = useCallback(
    (inning) => dispatch({ type: 'SET_CURRENT_INNING', inning }),
    []
  );
  const clearPendingInningEnd = useCallback(
    () => dispatch({ type: 'CLEAR_PENDING_INNING_END' }),
    []
  );
  const inheritedRunnerScored = useCallback(
    () => dispatch({ type: 'INHERITED_RUNNER_SCORED' }),
    []
  );
  const addPitchAction = useCallback(
    (payload) => dispatch({ type: 'ADD_PITCH_ACTION', payload }),
    []
  );
  const updatePitchAction = useCallback(
    (id, changes) => dispatch({ type: 'UPDATE_PITCH_ACTION', id, changes }),
    []
  );
  const deletePitchAction = useCallback(
    (id) => dispatch({ type: 'DELETE_PITCH_ACTION', id }),
    []
  );
  const undoLastPitch = useCallback(
    () => dispatch({ type: 'UNDO_LAST_PITCH' }),
    []
  );
  const setLastPitchVelocity = useCallback(
    (velocity) => dispatch({ type: 'SET_LAST_PITCH_VELOCITY', velocity }),
    []
  );
  const adjustRuns = useCallback(
    (value) => dispatch({ type: 'ADJUST_PITCHING_RUNS', value }),
    []
  );
  const adjustEarnedRuns = useCallback(
    (value) => dispatch({ type: 'ADJUST_PITCHING_EARNED_RUNS', value }),
    []
  );

  const setBattingStatus = useCallback(
    (status, sub) =>
      dispatch({ type: 'SET_BATTING_STATUS', status, substituteInning: sub }),
    []
  );
  const startAtBat = useCallback(
    (baseState, prefs) =>
      dispatch({ type: 'START_AT_BAT', baseState, trackingPrefs: prefs }),
    []
  );
  const updateAtBatCount = useCallback(
    (strikes, balls, baseState) =>
      dispatch({ type: 'UPDATE_AT_BAT_COUNT', strikes, balls, baseState }),
    []
  );
  const resetAtBat = useCallback(() => dispatch({ type: 'RESET_AT_BAT' }), []);
  const addBattingAction = useCallback(
    (payload) => dispatch({ type: 'ADD_BATTING_ACTION', payload }),
    []
  );
  const updateBattingAction = useCallback(
    (id, changes) => dispatch({ type: 'UPDATE_BATTING_ACTION', id, changes }),
    []
  );
  const deleteBattingAction = useCallback(
    (id) => dispatch({ type: 'DELETE_BATTING_ACTION', id }),
    []
  );
  const deleteAtBat = useCallback(
    (atBatId) => dispatch({ type: 'DELETE_BATTING_AT_BAT', atBatId }),
    []
  );
  const addCountAction = useCallback(
    (outcome, payload) => dispatch({ type: 'ADD_COUNT_ACTION', outcome, ...payload }),
    []
  );
  const undoLastCompletedAtBat = useCallback(
    () => dispatch({ type: 'UNDO_LAST_COMPLETED_AT_BAT' }),
    []
  );
  const undoLastBattingAction = useCallback(
    (pitchCounts) => dispatch({ type: 'UNDO_LAST_BATTING_ACTION', pitchCounts }),
    []
  );
  const setBattingTrackingPrefs = useCallback(
    (prefs) => dispatch({ type: 'SET_BATTING_TRACKING_PREFS', prefs }),
    []
  );

  const setActivePosition = useCallback(
    (pos, entryInning, entryOuts) => dispatch({ type: 'SET_ACTIVE_POSITION', position: pos, entryInning, entryOuts }),
    []
  );
  const updatePositionInnings = useCallback(
    (pos, innings) =>
      dispatch({ type: 'UPDATE_POSITION_INNINGS', position: pos, innings }),
    []
  );
  const setPositionEntry = useCallback(
    (pos, entryInning, entryOuts) =>
      dispatch({ type: 'SET_POSITION_ENTRY', position: pos, entryInning, entryOuts }),
    []
  );
  const setPositionExit = useCallback(
    (pos, exitInning, exitOuts) =>
      dispatch({ type: 'SET_POSITION_EXIT', position: pos, exitInning, exitOuts }),
    []
  );
  const donePitching = useCallback(
    (exitInning, exitOuts) => {
      dispatch({ type: 'DONE_PITCHING' });
      dispatch({ type: 'SET_POSITION_EXIT', position: 'P', exitInning, exitOuts });
    },
    []
  );
  const reactivatePitching = useCallback(
    () => {
      dispatch({ type: 'REACTIVATE_PITCHING' });
      dispatch({ type: 'CLEAR_POSITION_EXIT', position: 'P' });
    },
    []
  );
  const addFieldingAction = useCallback(
    (payload) => dispatch({ type: 'ADD_FIELDING_ACTION', payload }),
    []
  );
  const updateFieldingAction = useCallback(
    (id, changes) => dispatch({ type: 'UPDATE_FIELDING_ACTION', id, changes }),
    []
  );
  const deleteFieldingAction = useCallback(
    (id) => dispatch({ type: 'DELETE_FIELDING_ACTION', id }),
    []
  );
  const deleteFieldingPosition = useCallback(
    (pos) => dispatch({ type: 'DELETE_FIELDING_POSITION', position: pos }),
    []
  );

  return {
    state,
    dispatch,
    pitchingIP: selectPitchingIP(state),
    battingRatios: selectBattingRatios(state),
    fieldingTotals: selectFieldingTotals(state),
    pitchTypeStats: selectPitchTypeStats(state),
    commandTrend: selectCommandTrend(state),
    gamePayload: buildGamePayload(state),

    setPlayer,
    setGameInfo,
    setTracking,
    resetAll,
    restoreLiveState,
    startPitching,
    setPitchingRole,
    setPitchingDecision,
    setCurrentInning,
    setOpponentBase,
    clearOpponentBases,
    setInningPoisoned,
    setPhantomOuts,
    clearPendingInningEnd,
    inheritedRunnerScored,
    addPitchAction,
    updatePitchAction,
    deletePitchAction,
    undoLastPitch,
    resetPitching,
    setLastPitchVelocity,
    adjustRuns,
    adjustEarnedRuns,
    setBattingStatus,
    startAtBat,
    updateAtBatCount,
    addCountAction,
    resetAtBat,
    addBattingAction,
    updateBattingAction,
    deleteBattingAction,
    deleteAtBat,
    undoLastCompletedAtBat,
    undoLastBattingAction,
    setBattingTrackingPrefs,
    setActivePosition,
    updatePositionInnings,
    setPositionEntry,
    setPositionExit,
    donePitching,
    reactivatePitching,
    addFieldingAction,
    updateFieldingAction,
    deleteFieldingAction,
    deleteFieldingPosition,
  };
}
